'use client'

import { useEffect } from 'react'

const GLOBAL_MEDIA_SUSPEND_EVENT = 'ql7:global-media-suspend'
const EXTERNAL_VIDEO_STATE_EVENT = 'forum:external-video-state'

const YOUTUBE_RE = /(^|\.)youtube(?:-nocookie)?\.com$|(^|\.)youtu\.be$/i
const TIKTOK_RE = /(^|\.)tiktok\.com$/i
const BACKGROUND_PLAYBACK_ATTRIBUTE = 'data-ql7-background-playback'

function preservesBackgroundPlayback(element) {
  try {
    if (!element?.getAttribute) return false
    if (element.getAttribute(BACKGROUND_PLAYBACK_ATTRIBUTE) === 'continue') return true
    return !!element.closest?.(`[${BACKGROUND_PLAYBACK_ATTRIBUTE}="continue"]`)
  } catch {
    return false
  }
}

function safeUrl(raw) {
  try {
    return new URL(String(raw || ''), window.location.href)
  } catch {
    return null
  }
}

function markSystemPause(media) {
  try {
    media.dataset.__systemPause = '1'
    media.dataset.__systemPauseUntil = String(Date.now() + 60_000)
    media.dataset.__ql7VisibilityPaused = '1'
  } catch {}
}

function clearSystemPause(media) {
  try {
    if (media.dataset.__ql7VisibilityPaused !== '1') return
    delete media.dataset.__ql7VisibilityPaused
    delete media.dataset.__systemPause
    delete media.dataset.__systemPauseUntil
  } catch {}
}

function pauseHtmlMedia(root, visibilityPausedHtmlMedia = null) {
  if (!root?.querySelectorAll) return

  try {
    root.querySelectorAll('audio,video').forEach((media) => {
      if (!media || typeof media.pause !== 'function') return
      if (preservesBackgroundPlayback(media)) return
      if (visibilityPausedHtmlMedia && !visibilityPausedHtmlMedia.has(media)) {
        visibilityPausedHtmlMedia.set(media, {
          src: String(media.currentSrc || media.getAttribute?.('src') || ''),
          wasPlaying: !media.paused && !media.ended,
        })
      }
      markSystemPause(media)
      try { media.pause() } catch {}
    })
  } catch {}

}

function clearHtmlMediaSystemPause(root) {
  if (!root?.querySelectorAll) return

  try {
    root.querySelectorAll('audio,video').forEach((media) => {
      if (media && typeof media.pause === 'function') clearSystemPause(media)
    })
  } catch {}

}

function frameKind(frame) {
  const attr = String(
    frame.getAttribute('data-forum-media') ||
    frame.getAttribute('data-ad-media-kind') ||
    frame.getAttribute('data-kind') ||
    '',
  ).trim().toLowerCase()

  if (attr) return attr

  const src =
    frame.getAttribute('src') ||
    frame.getAttribute('data-src') ||
    ''
  const url = safeUrl(src)
  const host = String(url?.hostname || '').toLowerCase()

  if (YOUTUBE_RE.test(host)) return 'youtube'
  if (TIKTOK_RE.test(host)) return 'tiktok'
  return 'iframe'
}

function isMediaFrame(frame) {
  if (!(frame instanceof HTMLIFrameElement)) return false

  if (
    frame.hasAttribute('data-forum-media') ||
    frame.hasAttribute('data-ad-media-kind') ||
    frame.hasAttribute('data-ql7-media-iframe')
  ) {
    return true
  }

  const allow = String(frame.getAttribute('allow') || '').toLowerCase()
  return (
    allow.includes('autoplay')
  )
}

function emitExternalPlaybackState(frame, paused) {
  try {
    frame.dataset.__externalActualPlaying = paused ? '0' : '1'
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent(EXTERNAL_VIDEO_STATE_EVENT, {
      detail: { frame, paused: !!paused },
    }))
  } catch {}
}

function isKnownProviderFramePlaying(frame, kind) {
  if (!(frame instanceof HTMLIFrameElement)) return false

  if (kind === 'youtube') {
    try {
      const player = window.__forumYtPlayers?.get?.(frame)
      const state = Number(player?.getPlayerState?.())
      if (Number.isFinite(state)) return state === 1
    } catch {}
  }

  return String(frame.dataset?.__externalActualPlaying || '') === '1'
}

function pauseKnownProviderFrame(frame, kind) {
  if (!(frame instanceof HTMLIFrameElement)) return false

  if (kind === 'youtube') {
    try {
      const player = window.__forumYtPlayers?.get?.(frame)
      player?.pauseVideo?.()
    } catch {}

    try {
      frame.contentWindow?.postMessage?.(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*',
      )
    } catch {}

    emitExternalPlaybackState(frame, true)
    return true
  }

  if (kind === 'tiktok') {
    try {
      frame.contentWindow?.postMessage?.(
        { type: 'pause', 'x-tiktok-player': true },
        '*',
      )
    } catch {}

    emitExternalPlaybackState(frame, true)
    return true
  }

  return false
}

function resumeKnownProviderFrame(frame, kind) {
  if (!(frame instanceof HTMLIFrameElement)) return false

  if (kind === 'youtube') {
    try {
      const player = window.__forumYtPlayers?.get?.(frame)
      player?.playVideo?.()
    } catch {}

    try {
      frame.contentWindow?.postMessage?.(
        JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
        '*',
      )
    } catch {}

    emitExternalPlaybackState(frame, false)
    return true
  }

  if (kind === 'tiktok') {
    try {
      frame.contentWindow?.postMessage?.(
        { type: 'play', 'x-tiktok-player': true },
        '*',
      )
    } catch {}

    emitExternalPlaybackState(frame, false)
    return true
  }

  return false
}

function pauseSameOriginFrame(frame, visibilityPausedHtmlMedia = null) {
  try {
    const doc = frame.contentDocument
    if (!doc) return false
    pauseHtmlMedia(doc, visibilityPausedHtmlMedia)
    return true
  } catch {
    return false
  }
}

export default function GlobalMediaVisibilityRuntime() {
  useEffect(() => {
    let disposed = false
    const hardSuspendedFrames = new Map()
    const visibilityPausedHtmlMedia = new Map()
    const visibilityPausedProviderFrames = new Map()

    const hardSuspendUnknownMediaFrame = (frame) => {
      if (!(frame instanceof HTMLIFrameElement)) return
      if (hardSuspendedFrames.has(frame)) return

      const src = String(frame.getAttribute('src') || '').trim()
      if (!src || src === 'about:blank') return

      hardSuspendedFrames.set(frame, src)

      try {
        frame.dataset.__ql7VisibilitySuspended = '1'
        frame.setAttribute('src', 'about:blank')
      } catch {}
    }

    const suspendFrame = (frame) => {
      if (!(frame instanceof HTMLIFrameElement) || !isMediaFrame(frame)) return

      const kind = frameKind(frame)

      if (kind === 'youtube' || kind === 'tiktok') {
        if (isKnownProviderFramePlaying(frame, kind) && !visibilityPausedProviderFrames.has(frame)) {
          visibilityPausedProviderFrames.set(frame, {
            kind,
            src: String(frame.getAttribute('src') || frame.getAttribute('data-src') || ''),
          })
        }
        if (pauseKnownProviderFrame(frame, kind)) return
      }
      if (pauseSameOriginFrame(frame, visibilityPausedHtmlMedia)) return

      // A top-level page cannot directly pause arbitrary cross-origin iframe
      // media. For unknown media embeds, unloading is the only deterministic
      // way to guarantee that audio cannot continue behind a locked screen.
      hardSuspendUnknownMediaFrame(frame)
    }

    const suspendAll = (reason = 'hidden') => {
      if (disposed) return

      pauseHtmlMedia(document, visibilityPausedHtmlMedia)

      try {
        document.querySelectorAll('iframe').forEach(suspendFrame)
      } catch {}

      try {
        window.dispatchEvent(new CustomEvent(GLOBAL_MEDIA_SUSPEND_EVENT, {
          detail: { reason, ts: Date.now() },
        }))
      } catch {}
    }

    const restoreHardSuspendedFrames = () => {
      if (disposed || document.visibilityState !== 'visible') return

      for (const [frame, src] of hardSuspendedFrames) {
        hardSuspendedFrames.delete(frame)

        if (!(frame instanceof HTMLIFrameElement) || !frame.isConnected) continue

        try {
          if (frame.dataset.__ql7VisibilitySuspended === '1') {
            delete frame.dataset.__ql7VisibilitySuspended
            frame.setAttribute('src', src)
          }
        } catch {}
      }

      clearHtmlMediaSystemPause(document)

      for (const [media, entry] of visibilityPausedHtmlMedia) {
        visibilityPausedHtmlMedia.delete(media)
        clearSystemPause(media)
        if (!entry?.wasPlaying || !media || typeof media.play !== 'function' || !media.isConnected) continue

        const currentSrc = String(media.currentSrc || media.getAttribute?.('src') || '')
        if (entry.src && currentSrc && entry.src !== currentSrc) continue
        if (media.ended || media.dataset?.__userPaused === '1') continue

        try {
          const playPromise = media.play?.()
          playPromise?.catch?.(() => {})
        } catch {}
      }

      for (const [frame, entry] of visibilityPausedProviderFrames) {
        visibilityPausedProviderFrames.delete(frame)
        if (!(frame instanceof HTMLIFrameElement) || !frame.isConnected) continue

        const currentSrc = String(frame.getAttribute('src') || frame.getAttribute('data-src') || '')
        if (entry?.src && currentSrc && entry.src !== currentSrc) continue
        if (frame.dataset?.__userPaused === '1') continue
        resumeKnownProviderFrame(frame, entry?.kind)
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        suspendAll('visibility-hidden')
        return
      }

      restoreHardSuspendedFrames()
    }

    const onPageHide = () => {
      suspendAll('pagehide')
    }

    const onPageShow = () => {
      restoreHardSuspendedFrames()
    }

    const onFreeze = () => {
      suspendAll('freeze')
    }

    const onPlayCapture = (event) => {
      if (document.visibilityState !== 'hidden') return

      const target = event?.target
      if (!(target instanceof HTMLMediaElement)) return
      if (preservesBackgroundPlayback(target)) return

      if (!visibilityPausedHtmlMedia.has(target)) {
        visibilityPausedHtmlMedia.set(target, {
          src: String(target.currentSrc || target.getAttribute?.('src') || ''),
          wasPlaying: !target.paused && !target.ended,
        })
      }
      markSystemPause(target)
      try { target.pause() } catch {}
    }

    const onSiteMediaPlay = (event) => {
      if (document.visibilityState !== 'hidden') return

      const element = event?.detail?.element
      if (preservesBackgroundPlayback(element)) return

      if (element instanceof HTMLMediaElement) {
        if (!visibilityPausedHtmlMedia.has(element)) {
          visibilityPausedHtmlMedia.set(element, {
            src: String(element.currentSrc || element.getAttribute?.('src') || ''),
            wasPlaying: !element.paused && !element.ended,
          })
        }
        markSystemPause(element)
        try { element.pause() } catch {}
        return
      }

      if (element instanceof HTMLIFrameElement) {
        suspendFrame(element)
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange, true)
    window.addEventListener('pagehide', onPageHide, true)
    window.addEventListener('pageshow', onPageShow, true)
    document.addEventListener('freeze', onFreeze, true)
    document.addEventListener('play', onPlayCapture, true)
    window.addEventListener('site-media-play', onSiteMediaPlay, true)

    return () => {
      disposed = true

      document.removeEventListener('visibilitychange', onVisibilityChange, true)
      window.removeEventListener('pagehide', onPageHide, true)
      window.removeEventListener('pageshow', onPageShow, true)
      document.removeEventListener('freeze', onFreeze, true)
      document.removeEventListener('play', onPlayCapture, true)
      window.removeEventListener('site-media-play', onSiteMediaPlay, true)

      hardSuspendedFrames.clear()
      visibilityPausedHtmlMedia.clear()
      visibilityPausedProviderFrames.clear()
      clearHtmlMediaSystemPause(document)
    }
  }, [])

  return null
}
